import React, { useState, useEffect } from 'react';
import { Droppable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { DraggableTaskCard, TaskCardContent, createRenderTaskClone } from './DraggableTaskCard';
import { DraggableTicketCard, TicketCardContent, createRenderTicketClone, ticketAreaToTaskArea } from './DraggableTicketCard';
import { ServiceOrderWithTasks } from '../../types/serviceOrder';
import { Ticket } from '../../types/ticket';
import {
  Task,
  TaskArea,
  TaskAreaLabels,
  TaskAreaColors
} from '../../types/task';
import { getHoursBankStatus, getHoursBank } from '../../services/api';
import { HoursBankAreaData } from '../../types/hoursBank';

interface ServiceOrderManagementCardProps {
  serviceOrder: ServiceOrderWithTasks;
  projectId: string;
  tickets: Ticket[];
  onEditTask: (task: Task) => void;
  onDuplicateTask: (task: Task) => void;
  onEditTicket?: (ticket: Ticket) => void;
  onDeleteTicket?: (ticket: Ticket) => void;
}

type HoursBankData = HoursBankAreaData;

export const ServiceOrderManagementCard: React.FC<ServiceOrderManagementCardProps> = ({
  serviceOrder,
  projectId,
  tickets,
  onEditTask,
  onDuplicateTask,
  onEditTicket,
  onDeleteTicket
}) => {
  const [expandedAreas, setExpandedAreas] = useState<Record<TaskArea, boolean>>({
    frontend: true,
    backend: true,
    design: true,
    'Q&A': true
  });
  const [hoursTrackingEnabled, setHoursTrackingEnabled] = useState(false);
  const [hoursBankData, setHoursBankData] = useState<Record<TaskArea, HoursBankData>>({} as Record<TaskArea, HoursBankData>);

  useEffect(() => {
    loadHoursData();
  }, [projectId, serviceOrder]);

  const loadHoursData = async () => {
    try {
      // Check if hours tracking is enabled
      const statusResponse = await getHoursBankStatus(projectId);
      const enabled = statusResponse.data.data.hoursTrackingEnabled;
      setHoursTrackingEnabled(enabled);

      if (enabled) {
        // Use billing period from service order, not start date
        const month = serviceOrder.billingMonth;
        const year = serviceOrder.billingYear;

        // Load hours bank data
        const hoursResponse = await getHoursBank(projectId, year, month);
        if (hoursResponse.data.data.enabled && hoursResponse.data.data.hoursBank) {
          const bankData: Record<TaskArea, HoursBankData> = {} as Record<TaskArea, HoursBankData>;
          
          Object.entries(hoursResponse.data.data.hoursBank).forEach(([area, data]) => {
            if (data && typeof data === 'object' && 'allocatedHours' in data) {
              const areaData = data as HoursBankAreaData;
              bankData[area as TaskArea] = {
                allocatedHours: areaData.allocatedHours,
                usedHours: areaData.usedHours,
                availableHours: areaData.availableHours,
                utilizationPercentage: areaData.utilizationPercentage
              };
            }
          });
          
          setHoursBankData(bankData);
        }
      }
    } catch (error) {
      console.error('Error loading hours data:', error);
    }
  };

  const toggleArea = (area: TaskArea) => {
    setExpandedAreas(prev => ({
      ...prev,
      [area]: !prev[area]
    }));
  };

  // Always show all areas as droppable targets
  const allAreas: TaskArea[] = ['frontend', 'backend', 'design', 'Q&A'];

  // Collect all tasks from all areas for renderClone
  const allTasks = allAreas.reduce((acc, area) => {
    const areaData = serviceOrder.tasksByArea[area];
    if (areaData) {
      acc.push(...areaData.tasks);
    }
    return acc;
  }, [] as Task[]);

  // Group tickets by mapped area
  const ticketsByArea: Record<TaskArea, Ticket[]> = {
    frontend: [],
    backend: [],
    design: [],
    'Q&A': []
  };
  tickets.forEach(ticket => {
    const mappedArea = ticketAreaToTaskArea(ticket.area);
    ticketsByArea[mappedArea].push(ticket);
  });

  // Combined renderClone that handles both tasks and tickets
  const renderClone = (provided: DraggableProvided, snapshot: DraggableStateSnapshot, rubric: any) => {
    const draggableId = rubric.draggableId as string;

    if (draggableId.startsWith('ticket-')) {
      const ticketId = draggableId.replace('ticket-', '');
      const ticket = tickets.find(t => t._id === ticketId);
      if (!ticket) return null;
      return (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="mb-2 w-80"
          style={{ ...provided.draggableProps.style, width: '320px' }}
        >
          <TicketCardContent ticket={ticket} provided={provided} snapshot={snapshot} isDragDisabled={false} />
        </div>
      );
    }

    // Task clone
    const task = allTasks.find(t => t._id === draggableId);
    if (!task) return null;
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        className="mb-2 w-80"
        style={{ ...provided.draggableProps.style, width: '320px' }}
      >
        <TaskCardContent
          task={task}
          onEdit={onEditTask}
          onDuplicate={onDuplicateTask}
          provided={provided}
          snapshot={snapshot}
          isDragDisabled={false}
        />
      </div>
    );
  };

  return (
    <Card variant="glass" className="relative group">
      <CardContent className="p-4">
        {/* Header com nome da ordem de serviço */}
        <div className="mb-4 pb-3 border-b border-white/10">
          <h3 className="text-lg font-semibold text-foreground-primary">
            {serviceOrder.name}
          </h3>
          <p className="text-sm text-foreground-tertiary">
            Tarefas e chamados atribuídos à ordem de serviço
          </p>
        </div>

        {/* Tasks by area - always show all areas as drop targets */}
        {allAreas.map((area) => {
          const areaData = serviceOrder.tasksByArea[area];
          const tasks = areaData?.tasks || [];
          const areaTickets = ticketsByArea[area] || [];
          const taskHoras = areaData?.totalHoras || 0;
          const ticketHoras = areaTickets.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
          const totalHoras = taskHoras + ticketHoras;
          const totalItems = tasks.length + areaTickets.length;

          return (
            <div key={area} className="mb-4">
              {/* Area header */}
              <div
                className="flex items-center justify-between p-2 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => toggleArea(area)}
              >
                <div className="flex items-center gap-2">
                  {expandedAreas[area] ? (
                    <ChevronDown className="w-4 h-4 text-foreground-tertiary" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-foreground-tertiary" />
                  )}
                  <Badge className={`${TaskAreaColors[area]} text-sm`}>
                    {TaskAreaLabels[area]}
                  </Badge>
                  {/* Hours indicator */}
                  {hoursTrackingEnabled && hoursBankData[area] && (
                    <div className={`text-xs ${hoursBankData[area].availableHours < 0 ? 'text-red-400' : 'text-foreground-tertiary'}`}>
                      {totalHoras}h / {hoursBankData[area].availableHours}h {hoursBankData[area].availableHours < 0 ? 'excedido' : 'disponíveis'}
                    </div>
                  )}
                </div>

                {/* Hours availability status */}
                {hoursTrackingEnabled && hoursBankData[area] && (
                  <div className="flex items-center gap-2">
                    {hoursBankData[area].availableHours < 0 ? (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    ) : hoursBankData[area].availableHours < 5 ? (
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                    ) : null}
                    <div className={`text-xs ${hoursBankData[area].availableHours < 0 ? 'text-red-400' : 'text-foreground-tertiary'}`}>
                      {Math.round(hoursBankData[area].utilizationPercentage)}% usado
                    </div>
                  </div>
                )}
              </div>

              {/* Tasks + Tickets list - always show droppable area */}
              {expandedAreas[area] && (
                <div className="mt-2">
                  <Droppable
                    droppableId={`${serviceOrder._id}-${area}`}
                    type="task"
                    renderClone={renderClone}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`
                          min-h-[60px] w-full p-2 rounded-lg border-2 border-dashed transition-colors
                          ${snapshot.isDraggingOver
                            ? 'border-blue-500/50 bg-blue-500/10'
                            : 'border-white/10 bg-white/5'
                          }
                        `}
                      >
                        {tasks.map((task, index) => (
                          <DraggableTaskCard
                            key={task._id}
                            task={task}
                            index={index}
                            onEdit={onEditTask}
                            onDuplicate={onDuplicateTask}
                          />
                        ))}
                        {areaTickets.map((ticket, ticketIndex) => (
                          <DraggableTicketCard
                            key={ticket._id}
                            ticket={ticket}
                            index={tasks.length + ticketIndex}
                            onEdit={onEditTicket}
                            onDelete={onDeleteTicket}
                          />
                        ))}
                        {provided.placeholder}

                        {totalItems === 0 && (
                          <div className="text-center py-4 text-foreground-tertiary text-sm">
                            Arraste tarefas ou chamados de {TaskAreaLabels[area]} aqui
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              )}
            </div>
          );
        })}

      </CardContent>

    </Card>
  );
};